import {
  DeleteStockTakingSession,
  GetStockTakings,
  GetStockTakingSession,
  PostEndStockTakingSession,
  PostStockTaking,
  PutStockTakingSession,
  PostExportStockTakingInventoryList,
} from "@/server-types";
import { useCustomMutation } from "../custom-mutation";
import { del, get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { keepPreviousData } from "@tanstack/react-query";

export const usePostStockTakingSession = () => {
  return useCustomMutation({
    mutationFn: (body: PostStockTaking["body"]) =>
      post<PostStockTaking>(`/stock-taking`, body),
  });
};

export const useGetStockTakingSessions = (query: GetStockTakings["query"]) => {
  return useCustomQuery({
    queryKey: ["getStockTakingSessions", query],
    queryFn: () => get<GetStockTakings>(`/stock-taking`, query),
    placeholderData: keepPreviousData,
  });
};

export const useGetStockTakingSessionById = (sessionId: number) => {
  return useCustomQuery({
    queryKey: ["getStockTakingSessionById", sessionId],
    queryFn: () => get<GetStockTakingSession>(`/stock-taking/${sessionId}`),
  });
};

export const usePutStockTakingSessionById = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      sessionId,
    }: {
      body: PutStockTakingSession["body"];
      sessionId: number;
    }) => put<PutStockTakingSession>(`/stock-taking/${sessionId}`, body),
  });
};

export const useEndStockTakingSession = () => {
  return useCustomMutation({
    mutationFn: (sessionId: number) =>
      post<PostEndStockTakingSession>(`/stock-taking/${sessionId}/end`),
  });
};

export const useDeleteStockTakingSession = () => {
  return useCustomMutation({
    mutationFn: (sessionId: number) =>
      del<DeleteStockTakingSession>(`/stock-taking/${sessionId}`),
  });
};

export const usePostExportStockTakingInventoryList = () => {
  return useCustomMutation({
    mutationFn: async (props: {
      params: PostExportStockTakingInventoryList["params"];
      body: PostExportStockTakingInventoryList["body"];
    }) => {
      const response = await post<PostExportStockTakingInventoryList>(
        `/stock-taking/${props.params.stockTakingSessionId}/export/inventory-list`,
        props.body,
      );

      if (!response.data) return;

      // Convert base64 string to binary data
      let binaryString: string;
      try {
        binaryString = atob(response.data);
      } catch {
        throw new Error("Failed to decode PDF data");
      }
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and trigger download
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.filename || "inventory-list.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return response;
    },
  });
};
